/**
 * Ferina Cura Themes
 *
 * Jedes Theme definiert eine vollständige Menge an Farben; jede Farbe hat ein
 * klares Einsatzgebiet (siehe Kommentare). Keine Ableitung aus „Base of 4“ –
 * Farben werden den Einsatzgebieten direkt zugeordnet.
 */

/** Schatten-Style für Panels/Karten (React Native) */
export type PanelShadowStyle = {
  shadowColor: string;
  shadowOffset: { width: number; height: number };
  shadowOpacity: number;
  shadowRadius: number;
  elevation: number;
};

/** Kein sichtbarer Schatten – große Panels bleiben flach. */
export const NO_PANEL_SHADOW: PanelShadowStyle = {
  shadowColor: "#000",
  shadowOffset: { width: 0, height: 0 },
  shadowOpacity: 0,
  shadowRadius: 0,
  elevation: 0,
};

/** Sehr zurückhaltende Kartentiefe (Light) – keine Admin-Backend-Schatten. */
export const SOFT_CARD_SHADOW_LIGHT: PanelShadowStyle = {
  shadowColor: "#0F1722",
  shadowOffset: { width: 0, height: 1 },
  shadowOpacity: 0.05,
  shadowRadius: 8,
  elevation: 1,
};

/** Zurückhaltende Kartentiefe (Dark). */
export const SOFT_CARD_SHADOW_DARK: PanelShadowStyle = {
  shadowColor: "#000000",
  shadowOffset: { width: 0, height: 1 },
  shadowOpacity: 0.28,
  shadowRadius: 10,
  elevation: 2,
};

export type Theme = {
  mode: "light" | "dark";
  color: {
    // —— Hauptfarben (Logo, Navbar, hervorgehobene UI) ——
    /** Logo, Navbar, Primär-Buttons, aktive Badges, Hero-Dots */
    primary: string;
    /** Gestrichelte Ränder (z. B. Card variant="dashed"), abgeschwächtes Primary */
    primaryMuted: string;
    /** Sehr dezent für Trennlinien / Sekundärflächen */
    secondary: string;

    // —— Text ——
    /** Fließtext, Überschriften */
    text: string;
    /** Zweite Zeile, Beschreibungstext */
    textSecondary: string;
    /** Weniger hervorgehobener Text (Kicker, Metadaten) */
    muted: string;
    /** Platzhalter in leeren Feldern */
    placeholder: string;
    /** Kleingedruckt (z. B. Datenschutz-Hinweis auf Login) */
    fine: string;
    /** Footer-Text (z. B. © Impressum) */
    footer: string;

    // —— Input-Felder ——
    /** Hintergrund von TextInput / FloatingLabelInput */
    inputBg: string;
    /** Rahmenfarbe Input (unfokussiert) */
    inputBorder: string;
    /** Rahmenfarbe Input bei Fokus */
    inputFocusBorder: string;
    /** Platzhalter-Text im Input */
    inputPlaceholder: string;
    /** Hintergrund des schwebenden Labels (über dem Rahmen) */
    inputLabelBg: string;
    /** Icon im Input (z. B. Auge bei Passwort) */
    inputIcon: string;
    /** Farbe des schwebenden Labels wenn nicht fokussiert */
    inputLabelFloatingColor: string;

    // —— Hintergründe ——
    /** App-Hintergrund (Seiten-BG) */
    bg: string;
    /** Kräftiger Hintergrund (Sidebar, starke Flächen) */
    bgStrong: string;
    /** Karten, Panels, Login-Panel-Hintergrund */
    surface: string;
    /** Hervorgehobene Flächen (z. B. Login-Formular-Card) */
    surfaceStrong: string;
    /** Hover-Zustand auf Flächen */
    surfaceHover: string;
    /** Inhalt von FileTabs/Panel (hell: weiß, dunkel: surface) */
    panelContentBg: string;
    /** Inhalt (Body) von SectionCards – hell: weiß, dunkel: surface */
    sectionCardBodyBg: string;
    /** Hero-Textbereich unter dem Bild (Login) */
    heroPanelBg: string;
    /** Pagination-Container über dem Hero (Login) */
    heroPaginationBg: string;

    // —— Rahmen & Overlay ——
    /** Standard-Rahmen (Karten, Panels, Inputs) */
    border: string;
    /** Kräftiger Rahmen */
    borderStrong: string;
    /** Modal-Overlay-Hintergrund */
    overlay: string;

    // —— Akzent (Links, sekundäre CTAs) ——
    /** Links, „Passwort vergessen“, TextButton, Outline-Button-Rand */
    accent: string;
    /** Abgeschwächter Akzent */
    accentMuted: string;
    /** Hintergrund für Akzent-Bereiche (z. B. Icon-Box) */
    accentBg: string;

    // —— Semantische Farben ——
    /** Erfolg (z. B. gespeichert) */
    success: string;
    successBg: string;
    /** Info-Hinweise */
    info: string;
    infoBg: string;
    /** Warnung */
    warning: string;
    warningBg: string;
    /** Fehler, Löschen, Danger-Buttons (Fläche) */
    danger: string;
    dangerBg: string;
    /** Fehlertext. Fläche bleibt `danger`. */
    dangerText: string;
    /**
     * Gefüllter Destruktiv-Button nur im Bestätigungsdialog.
     * Weiß auf `danger` verfehlt 4,5:1; dieses Token hält Weiß darüber.
     */
    dangerButtonBg: string;
    /** Warntext. Fläche bleibt `warning`. */
    warningText: string;
    /** Erfolgtext auf `successBg`. Fläche bleibt `success`. */
    successText: string;
    /** Infotext auf `infoBg`. Fläche bleibt `info`. */
    infoText: string;

    // —— Buttons ——
    /** Hintergrund Primär-Button */
    buttonBg: string;
    /** Text auf Primär-Button */
    buttonText: string;
    /** Deckende Fläche deaktivierter Primary-, Secondary- und Danger-Buttons sowie gewählter Outline/Ghost */
    buttonDisabledBg: string;
    /** Beschriftung auf der deckenden Disabled-Fläche */
    buttonDisabledText: string;
    /** Text, Icon und 1px-Rahmen deaktivierter, nicht gewählter Outline- und Ghost-Buttons */
    buttonDisabledSubtle: string;
    /** Rahmen einer gewählten, deaktivierten Option (≥3:1 auf buttonDisabledBg) */
    buttonDisabledSelectedBorder: string;
    /** Hintergrund Sekundär-Button (z. B. Abbrechen) */
    buttonBgSecondary: string;
    buttonTextSecondary: string;
    /** Hintergrund Outline-Button (z. B. „Zur Registrierung“) */
    outlineButtonBg: string;
    /** Rahmen Outline-Button */
    outlineButtonBorder: string;

    // —— Links ——
    /** Standard-Linkfarbe (Datenschutz, Nutzungsbedingungen) */
    link: string;
    linkHover: string;

    // —— Konstanten ——
    white: string;
    black: string;

    // —— Badges ——
    badgeBg: string;
    badgeText: string;
    badgeMutedBg: string;
    badgeMutedText: string;
  };
  radius: {
    sm: number;
    md: number;
    lg: number;
    xl: number;
    full: number;
    /** Große Panels (z. B. Login-Karten) */
    panel: number;
    /** Karten (Formular-Card, Register-Card) */
    card: number;
    /** Dichte Felder, Segmente und Chips. Entspricht `sm`. */
    control: number;
  };
  space: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
    xxl: number;
    /** Innenabstand Formular-Card */
    cardPadding: number;
    /** Innenabstand Register-Card */
    registerPadding: number;
    /** Label zum Feld, Abstand zwischen Chips. */
    dense: number;
    /** Zeilenabstand in Formularen. */
    formRow: number;
  };
  /** Feldmaße. Die Login-Werte bleiben die bisherigen. */
  control: {
    /** Bisherige Feldhöhe (Login und bestehende Formulare). */
    height: number;
    /** Kompakte Felder, Web-Desktop. */
    heightDense: number;
    /** Tablet, Phone und nativ. */
    heightTouch: number;
    /** Bisheriger Feldradius (Login). Dichte Felder nutzen `radius.control`. */
    radius: number;
    /** Bisheriges horizontales Feldpolster (Login). */
    paddingH: number;
    /** Bisheriges vertikales Feldpolster (Login). */
    paddingV: number;
  };
  /** Zeilenhöhen für Tabellen. */
  table: {
    rowDense: number;
    rowTouch: number;
    /** B2: Desktop-Zeile der Pflegefall-Tabelle. rowDense bleibt 32. */
    rowCase: number;
    /** B2: Phone-Zeile, die ganze Zeile öffnet die Akte. */
    rowCasePhone: number;
  };
  /** Inhaltsbreiten, die keine Abstände sind. */
  layout: {
    /** Lesbare Spaltenbreite für Hinweis- und Fehlerflächen. */
    readable: number;
    /** Maximale Breite von Formularseiten. */
    formMax: number;
    /** Schmale Felder: Gewicht. */
    fieldWeight: number;
    /** Schmale Felder: Datum. */
    fieldDate: number;
  };
  fontSize: {
    title: number;
    h2: number;
    h3: number;
    body: number;
    small: number;
    tiny: number;
    /** Kleingedruckt (fine) */
    fine: number;
    /** Footer */
    footer: number;
    /** Feldlabel über dem Feld. */
    label: number;
  };
  fineLineHeight: number;
  fontFamily: string;
  shadow: {
    color: string;
    opacity: number;
    radius: number;
    offset: { x: number; y: number };
  };
  /** Schatten für große Panels (Login-Hero, Login-Panel) */
  panelShadow: PanelShadowStyle;
  /** Schatten für Karten (Formular-Card) */
  cardShadow: Partial<PanelShadowStyle>;
  /** Schatten für Primär-Button (z. B. Anmelden) */
  primaryButtonShadow: Partial<PanelShadowStyle>;
  /** Schatten für Outline-Button */
  outlineButtonShadow: Partial<PanelShadowStyle>;
  /** Schatten bei Input-Fokus (leer = keiner) */
  inputFocusShadow: Partial<PanelShadowStyle>;
  /** Nur Web Light: radial gradient für Seitenhintergrund (Login) */
  pageBgWebGradient?: string;
  /** true = flacher, ruhiger Seiten-Hintergrund ohne Verlauf (moderner Look). */
  flatBackground?: boolean;
};

/** Kontrast auf dunklem Input: Platzhalter/Label/Icon – gut lesbar („schmuddeliges Hellgrau“). */
const INPUT_ON_DARK_FG = "rgba(244,246,249,.82)";

// =============================================================================
// Gemeinsame Werte (Radius, Abstände, Schriftgrößen) – für alle Themes gleich
// =============================================================================

const themeBase = {
  radius: {
    sm: 6,
    md: 8,
    lg: 12,
    xl: 16,
    full: 9999,
    panel: 14,
    card: 12,
    control: 6,
  },
  space: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    xxl: 28,
    cardPadding: 14,
    registerPadding: 14,
    dense: 6,
    formRow: 12,
  },
  control: {
    height: 48,
    heightDense: 36,
    heightTouch: 44,
    radius: 12,
    paddingH: 16,
    paddingV: 12,
  },
  table: {
    rowDense: 32,
    rowTouch: 44,
    rowCase: 40,
    rowCasePhone: 52,
  },
  layout: {
    readable: 560,
    formMax: 960,
    fieldWeight: 120,
    fieldDate: 150,
  },
  fontSize: {
    title: 22,
    h2: 18,
    h3: 16,
    body: 15,
    small: 13,
    tiny: 11,
    fine: 12,
    footer: 12,
    label: 12,
  },
  fineLineHeight: 1.4,
  fontFamily: "System",
} as const;

// =============================================================================
// Standard Light – vollständige Farbdefinition mit Kommentar pro Einsatzgebiet
// =============================================================================

// Standard Light – Kontrast testweise erhöht (Text dunkler, Rahmen kräftiger, muted lesbarer)
const STANDARD_LIGHT_COLORS: Theme["color"] = {
  primary: "#7fa0b4", // Logo, Navbar, Primär-Buttons, Hero-Dots
  primaryMuted: "rgba(127,160,180,.55)", // Gestrichelte Ränder (Register-Card)
  secondary: "rgba(42,63,75,.06)", // Dezent

  text: "#0F1722", // Fließtext, Überschriften (dunkler für besseren Kontrast)
  textSecondary: "rgba(15,23,34,.85)", // Beschreibungstext
  muted: "#475562", // Kicker, Metadaten (kräftiger)
  placeholder: "#5a6c78", // Platzhalter
  fine: "#5a6c78", // Kleingedruckt (Datenschutz auf Login)
  footer: "#5a6c78", // Footer ©

  inputBg: "rgba(255,255,255,.94)", // Hintergrund TextInput
  inputBorder: "#5E7380", // Rahmen Input – 4.95:1 auf Weiß, 3.52:1 auf #CFDBE4
  inputFocusBorder: "#4F6D7E", // Fokus-Rahmen/Label – 5.50:1 auf Weiß, 3.90:1 auf #CFDBE4
  inputPlaceholder: "#3d5262", // Platzhalter/Label – kontrastreicher
  inputLabelBg: "rgba(255,255,255,.92)", // Hintergrund schwebendes Label (unverändert)
  inputIcon: "#3d5262", // Auge (Passwort anzeigen)
  inputLabelFloatingColor: "#2a3f4b", // Label schwebend – kontrastreicher

  bg: "#CFDBE4", // App-Hintergrund
  bgStrong: "#93AEC2", // Sidebar, starke Flächen
  surface: "rgba(255,255,255,.72)", // Login-Panel, Karten (leicht deckender)
  surfaceStrong: "rgba(255,255,255,.96)", // Formular-Card
  surfaceHover: "rgba(0,0,0,.06)", // Hover
  panelContentBg: "#FFFFFF", // FileTabs-Inhalt, SectionCards darin
  sectionCardBodyBg: "#FFFFFF", // SectionCard-Inhalt (unten)
  heroPanelBg: "rgba(255,255,255,.56)", // Hero-Text unter Bild
  heroPaginationBg: "rgba(255,255,255,.45)", // Pagination-Container

  border: "rgba(28,55,72,.22)", // Rahmen Karten, Panels (kräftiger)
  borderStrong: "rgba(28,55,72,.32)",
  overlay: "rgba(15,27,34,0.55)", // Modal

  accent: "#d8742a", // „Passwort vergessen“, TextButton
  accentMuted: "rgba(216,116,42,.2)",
  accentBg: "#CFDBE4", // Icon-Box-Hintergrund

  success: "#3FB98A",
  successBg: "rgba(63,185,138,.12)",
  info: "#4A7896",
  infoBg: "rgba(74,120,150,.12)",
  warning: "#006be6",
  warningBg: "rgba(0,107,230,.14)",
  warningText: "#006be6",
  danger: "#cc1111",
  dangerBg: "rgba(204,17,17,.1)",
  dangerText: "#cc1111",
  dangerButtonBg: "#A4494A",
  successText: "#3FB98A", // gleich success, bisherige Chip-Farbe
  infoText: "#4A7896", // gleich info, bisherige Chip-Farbe

  buttonBg: "#4F6D7E", // Primär-Button – Weiß-Label 5.50:1 (Default/Hover/Focus; vorher #7fa0b4 ≈ 2.77:1)
  buttonText: "#FFFFFF",
  buttonDisabledBg: "#D5E0E6", // Disabled-Fläche, deckend
  buttonDisabledText: "#4F6570", // Disabled-Label 4.56:1 auf #D5E0E6
  buttonDisabledSubtle: "#5C7280", // Outline/Ghost 4.59:1 auf der Karte, 3.57:1 auf #CFDBE4, 5.03:1 auf Weiß
  buttonDisabledSelectedBorder: "#1B2B33", // Auswahlrahmen 10.86:1 auf #D5E0E6
  buttonBgSecondary: "rgba(127,160,180,.15)",
  buttonTextSecondary: "#2a3f4b",
  outlineButtonBg: "rgba(127,160,180,.10)", // „Zur Registrierung“
  outlineButtonBorder: "rgba(127,160,180,.45)",

  link: "#7fa0b4", // Datenschutz, Nutzungsbedingungen
  linkHover: "rgba(127,160,180,.78)",

  white: "#FFFFFF",
  black: "#0B1116",

  badgeBg: "#7fa0b4",
  badgeText: "#FFFFFF",
  badgeMutedBg: "rgba(127,160,180,.15)",
  badgeMutedText: "#475562",
};

const STANDARD_LIGHT: Omit<Theme, "mode"> = {
  ...themeBase,
  color: STANDARD_LIGHT_COLORS,
  shadow: {
    color: "#0F1722",
    opacity: 0.07,
    radius: 12,
    offset: { x: 0, y: 2 },
  },
  panelShadow: NO_PANEL_SHADOW,
  cardShadow: {},
  primaryButtonShadow: {},
  outlineButtonShadow: {},
  inputFocusShadow: {},
  pageBgWebGradient:
    "radial-gradient(1100px 600px at 12% 0%, #eef5fa 0%, rgba(127, 160, 180, 0.25) 75%, rgba(127, 160, 180, 0.59) 100%)",
};

// =============================================================================
// Standard Dark – vollständige Farbdefinition mit Kommentar pro Einsatzgebiet
// =============================================================================

const STANDARD_DARK_COLORS: Theme["color"] = {
  primary: "#4E83A6", // Logo, Navbar, aktive Tabs/Pills – dunkel genug für weißen Text
  primaryMuted: "rgba(78,131,166,.5)", // Gestrichelte Ränder
  secondary: "rgba(255,255,255,.06)", // Dezent

  text: "#F4F6F9", // Fließtext
  textSecondary: "rgba(244,246,249,.78)", // Beschreibung
  muted: "rgba(244,246,249,.58)", // Kicker, Metadaten
  placeholder: "rgba(244,246,249,.42)", // Platzhalter
  fine: "rgba(244,246,249,.58)", // Kleingedruckt
  footer: "rgba(244,246,249,.58)", // Footer

  inputBg: "#2A4153", // Hintergrund TextInput
  inputBorder: "#8AA0B0", // Rahmen Input – 3.91:1 auf #2A4153, 4.87:1 auf #20323F
  inputFocusBorder: "#2DD4BF", // Fokus-Rahmen/Label – 5.70:1 auf #2A4153 (vorher #2FB6A8 ≈ 4.23:1)
  inputPlaceholder: INPUT_ON_DARK_FG, // Platzhalter/Label – gut lesbar auf dunklem Input
  inputLabelBg: "#2A4153", // Schwebendes Label
  inputIcon: INPUT_ON_DARK_FG, // Auge (Passwort) – gut lesbar
  inputLabelFloatingColor: "#2FB6A8", // Label schwebend

  bg: "#20323F", // App-Hintergrund
  bgStrong: "#355369", // Sidebar
  surface: "#2A4153", // Karten, Panels
  surfaceStrong: "#2A4153", // Formular-Card
  surfaceHover: "rgba(255,255,255,.08)", // Hover
  panelContentBg: "#2A4153", // FileTabs-Inhalt (wie surface)
  sectionCardBodyBg: "#2A4153", // SectionCard-Inhalt (wie surface)
  heroPanelBg: "rgba(0,0,0,.4)", // Hero-Text unter Bild
  heroPaginationBg: "rgba(255,255,255,.45)", // Pagination

  border: "rgba(255,255,255,.12)", // Rahmen
  borderStrong: "rgba(255,255,255,.22)",
  overlay: "rgba(8,12,16,0.72)", // Modal

  accent: "#2FB6A8", // Links, Passwort vergessen, Outline-Rand (medizinisches Türkis)
  accentMuted: "rgba(47,182,168,.18)",
  accentBg: "#20323F", // Icon-Box

  success: "#3FB98A",
  successBg: "rgba(63,185,138,.18)",
  info: "rgba(144,178,200,.7)",
  infoBg: "rgba(144,178,200,.18)",
  warning: "#E0A33A", // Amber – klar als Warnung lesbar
  warningBg: "rgba(224,163,58,.18)",
  warningText: "#E0A33A",
  danger: "#E17672",
  dangerBg: "rgba(225,118,114,.18)",
  dangerText: "#E17672",
  dangerButtonBg: "#B45452", // angehoben: Weiß 4,85:1; gegen #2A4153 nur 2,19:1 (Karte heller als Care)
  successText: "#3FB98A", // gleich success, bisherige Chip-Farbe
  infoText: "rgba(144,178,200,.7)", // gleich info, bisherige Chip-Farbe

  buttonBg: "#0F766E", // Primär-Button – Weiß-Label 5.47:1 (Default/Hover/Focus; vorher #1A9488 ≈ 3.73:1)
  buttonText: "#FFFFFF",
  buttonDisabledBg: "#243848", // Disabled-Fläche, deckend
  buttonDisabledText: "#97AAB6", // Disabled-Label 5.04:1 auf #243848
  buttonDisabledSubtle: "#9BACB7", // Outline/Ghost 4.54:1 auf #2A4153, 5.65:1 auf #20323F
  buttonDisabledSelectedBorder: "#D5E2EA", // Auswahlrahmen 9.17:1 auf #243848
  buttonBgSecondary: "rgba(53,83,105,.9)", // Sekundär-Button
  buttonTextSecondary: "#F4F6F9",
  outlineButtonBg: "transparent", // Outline-Button
  outlineButtonBorder: "#2FB6A8",

  link: "#2FB6A8",
  linkHover: "rgba(47,182,168,.78)",

  white: "#FFFFFF",
  black: "#0B1116",

  badgeBg: "#1A9488",
  badgeText: "#FFFFFF",
  badgeMutedBg: "rgba(53,83,105,.9)",
  badgeMutedText: "rgba(244,246,249,.58)",
};

const STANDARD_DARK: Omit<Theme, "mode"> = {
  ...themeBase,
  color: STANDARD_DARK_COLORS,
  shadow: {
    color: "#000000",
    opacity: 0.32,
    radius: 16,
    offset: { x: 0, y: 4 },
  },
  panelShadow: NO_PANEL_SHADOW,
  cardShadow: {},
  primaryButtonShadow: {},
  outlineButtonShadow: {},
  inputFocusShadow: {},
  pageBgWebGradient: undefined,
};

// =============================================================================
// Klar – neues Signatur-Theme: flach, kontrastreich, dicht, medizinisches Teal.
// Ziel: „Werkzeug statt Schaufenster“ – alles im Blick, schnell zur richtigen Info.
// =============================================================================

/** Dichtere Tokens als themeBase: kleinere Radien, engere Abstände, kompaktere Schrift. */
const klarBase = {
  ...themeBase,
  radius: {
    sm: 4,
    md: 6,
    lg: 8,
    xl: 12,
    full: 9999,
    panel: 10,
    card: 8,
    control: 4,
  },
  space: {
    xs: 4,
    sm: 8,
    md: 10,
    lg: 14,
    xl: 16,
    xxl: 22,
    cardPadding: 12,
    registerPadding: 14,
    dense: 6,
    formRow: 12,
  },
  fontSize: {
    title: 20,
    h2: 17,
    h3: 15,
    body: 14,
    small: 13,
    tiny: 11,
    fine: 12,
    footer: 12,
    label: 12,
  },
} as const;

const KLAR_LIGHT_COLORS: Theme["color"] = {
  primary: "#0F766E", // Brand-Teal (Navbar, aktive UI, Logo)
  primaryMuted: "rgba(15,118,110,.45)",
  secondary: "#EDF1F4",

  text: "#0E1A20", // hoher Kontrast auf Weiß
  textSecondary: "#33454F",
  muted: "#5B6B76",
  placeholder: "#8494A0",
  fine: "#5B6B76",
  footer: "#5B6B76",

  inputBg: "#FFFFFF", // deckend, kein Milchglas
  inputBorder: "#6A7C88", // Rahmen Input – 4.33:1 auf Weiß, 3.88:1 auf #F1F3F2
  inputFocusBorder: "#0F766E", // Fokus-Rahmen – 5.47:1 auf Weiß, 4.91:1 auf #F1F3F2
  inputPlaceholder: "#5B6B76", // 5,51:1 auf Weiß, gleicher Ton wie muted
  inputLabelBg: "#FFFFFF",
  inputIcon: "#5B6B76",
  inputLabelFloatingColor: "#0F766E",

  bg: "#F1F3F2", // ruhige, leicht warme Arbeitsfläche
  bgStrong: "#E6EBE9", // helle Füllfläche (Pills/Toggles/Icon-Boxen)
  surface: "#FFFFFF", // deckende Karten/Panels
  surfaceStrong: "#FFFFFF",
  surfaceHover: "#EEF3F2",
  panelContentBg: "#FFFFFF",
  sectionCardBodyBg: "#FFFFFF",
  heroPanelBg: "rgba(255,255,255,.9)",
  heroPaginationBg: "rgba(255,255,255,.55)",

  border: "#DCE3E1", // klare, deckende Trennlinien
  borderStrong: "#C2CDC9",
  overlay: "rgba(9,18,24,.55)",

  accent: "#0F766E", // Links, aktive Zustände, Fokus
  accentMuted: "rgba(15,118,110,.22)",
  accentBg: "rgba(15,118,110,.10)", // Teal-Tönung für Icon-Boxen

  success: "#0E9F6E",
  successBg: "rgba(14,159,110,.12)",
  successText: "#0A7551", // 4,515:1 auf successBg über #F1F3F2, 4,990:1 über der Karte
  info: "#0284C7",
  infoBg: "rgba(2,132,199,.12)",
  infoText: "#026BA1", // 4,506:1 auf infoBg über #F1F3F2, 4,983:1 über der Karte
  warning: "#B86A2B", // Fläche bleibt
  warningBg: "rgba(184,106,43,.12)",
  warningText: "#975523", // 5,177:1 auf #F1F3F2, 4,522:1 auf warningBg über #F1F3F2
  danger: "#C45B5B", // Fläche bleibt
  dangerBg: "rgba(196,91,91,.12)",
  dangerText: "#A4494A", // 5,190:1 auf #F1F3F2, 4,510:1 auf dangerBg über #F1F3F2
  dangerButtonBg: "#A4494A", // Weiß 5,78:1; gefüllt nur im Bestätigungsdialog

  buttonBg: "#0F766E", // Primär-Button (weißer Text gut lesbar)
  buttonText: "#FFFFFF",
  buttonDisabledBg: "#D7E7E4", // Disabled-Fläche, deckend
  buttonDisabledText: "#4A6461", // Disabled-Label 5.00:1 auf #D7E7E4
  buttonDisabledSubtle: "#667A77", // Outline/Ghost 4.55:1 auf Weiß, 4.08:1 auf #F1F3F2
  buttonDisabledSelectedBorder: "#143E3B", // Auswahlrahmen 9.23:1 auf #D7E7E4
  buttonBgSecondary: "#E7EDF0",
  buttonTextSecondary: "#1B2B33",
  outlineButtonBg: "transparent",
  outlineButtonBorder: "#0F766E",

  link: "#0F766E",
  linkHover: "#0B5A54",

  white: "#FFFFFF",
  black: "#0B1116",

  badgeBg: "#0F766E",
  badgeText: "#FFFFFF",
  badgeMutedBg: "#E7EDF0",
  badgeMutedText: "#5B6B76",
};

const KLAR_DARK_COLORS: Theme["color"] = {
  primary: "#0D9488", // tiefes Teal als Füllung (weißer Text/Icon bleibt lesbar); accent bleibt hell für Links
  primaryMuted: "rgba(13,148,136,.45)",
  secondary: "rgba(255,255,255,.06)",

  text: "#EAF1F4",
  textSecondary: "rgba(234,241,244,.78)",
  muted: "rgba(234,241,244,.56)",
  placeholder: "rgba(234,241,244,.4)",
  fine: "rgba(234,241,244,.56)",
  footer: "rgba(234,241,244,.56)",

  inputBg: "#1B2A3A",
  inputBorder: "#8AA0B0", // Rahmen Input – ≥5.3:1 auf #1B2836 / #1B2A3A, 6.70:1 auf #0E1621
  inputFocusBorder: "#2DD4BF", // Fokus-Rahmen – ≥7:1 auf den dunklen Kartenflächen
  inputPlaceholder: INPUT_ON_DARK_FG,
  inputLabelBg: "#1B2A3A",
  inputIcon: INPUT_ON_DARK_FG,
  inputLabelFloatingColor: "#2DD4BF",

  bg: "#0E1621", // flacher dunkler BG
  bgStrong: "#22303F", // erhöhte Füllfläche
  surface: "#16212E", // deckende Karten
  surfaceStrong: "#1B2836",
  surfaceHover: "rgba(255,255,255,.06)",
  panelContentBg: "#16212E",
  sectionCardBodyBg: "#16212E",
  heroPanelBg: "rgba(0,0,0,.4)",
  heroPaginationBg: "rgba(255,255,255,.45)",

  border: "rgba(255,255,255,.1)",
  borderStrong: "rgba(255,255,255,.2)",
  overlay: "rgba(6,10,14,.72)",

  accent: "#2DD4BF",
  accentMuted: "rgba(45,212,191,.2)",
  accentBg: "rgba(45,212,191,.14)",

  success: "#34D399",
  successBg: "rgba(52,211,153,.18)",
  successText: "#34D399", // gleich success; Chip-Tönung über Seite und Karte ≥ 4,5:1
  info: "#38BDF8",
  infoBg: "rgba(56,189,248,.16)",
  infoText: "#38BDF8", // gleich info; Chip-Tönung über Seite und Karte ≥ 4,5:1
  warning: "#E0A33A",
  warningBg: "rgba(224,163,58,.18)",
  warningText: "#E0A33A", // 7,33:1 auf #16212E
  danger: "#F0736E",
  dangerBg: "rgba(240,115,110,.18)",
  dangerText: "#F3756E", // 5,87:1 auf #16212E, 4,51:1 auf dangerBg über der Karte
  dangerButtonBg: "#B45452", // angehoben von #A4494A: Weiß 4,85:1, gegen die Karte #16212E 3,35:1

  buttonBg: "#0F766E", // Primär-Button – Weiß-Label 5.47:1 (Default/Hover/Focus; vorher #0D9488 ≈ 3.74:1)
  buttonText: "#FFFFFF",
  buttonDisabledBg: "#1C3338", // Disabled-Fläche, deckend
  buttonDisabledText: "#8FA5A2", // Disabled-Label 5.11:1 auf #1C3338
  buttonDisabledSubtle: "#8AA19E", // Outline/Ghost 5.94:1 auf #16212E, 5.47:1 auf #1B2836, 6.64:1 auf #0E1621
  buttonDisabledSelectedBorder: "#D5E4E2", // Auswahlrahmen 10.14:1 auf #1C3338
  buttonBgSecondary: "#26333F",
  buttonTextSecondary: "#EAF1F4",
  outlineButtonBg: "transparent",
  outlineButtonBorder: "#2DD4BF",

  link: "#2DD4BF",
  linkHover: "rgba(45,212,191,.8)",

  white: "#FFFFFF",
  black: "#0B1116",

  badgeBg: "#0D9488",
  badgeText: "#FFFFFF",
  badgeMutedBg: "#26333F",
  badgeMutedText: "rgba(234,241,244,.56)", // Chip: Alpha über badgeMutedBg, 4,696:1 auf Seite, Karte und Surface
};

const KLAR_LIGHT: Omit<Theme, "mode"> = {
  ...klarBase,
  color: KLAR_LIGHT_COLORS,
  shadow: { color: "#0F1722", opacity: 0.05, radius: 8, offset: { x: 0, y: 1 } },
  panelShadow: NO_PANEL_SHADOW,
  cardShadow: SOFT_CARD_SHADOW_LIGHT,
  primaryButtonShadow: {},
  outlineButtonShadow: {},
  inputFocusShadow: {},
  pageBgWebGradient: undefined,
  flatBackground: true,
};

const KLAR_DARK: Omit<Theme, "mode"> = {
  ...klarBase,
  color: KLAR_DARK_COLORS,
  shadow: { color: "#000000", opacity: 0.3, radius: 12, offset: { x: 0, y: 2 } },
  panelShadow: NO_PANEL_SHADOW,
  cardShadow: SOFT_CARD_SHADOW_DARK,
  primaryButtonShadow: {},
  outlineButtonShadow: {},
  inputFocusShadow: {},
  pageBgWebGradient: undefined,
  flatBackground: true,
};

// =============================================================================
// Weitere Presets (Wald, Ozean, …) – aus Palettenfarben aufgebaut
// Jedes Preset definiert 4 Palettenfarben; daraus wird die volle Farbmenge
// für den Einsatz in den gleichen Einsatzgebieten erzeugt.
// =============================================================================

type Palette = {
  /** App-Hintergrund */
  bg: string;
  /** Karten, Panels, Surface */
  surface: string;
  /** Primär (Logo, Buttons, Navbar) */
  primary: string;
  /** Akzent (Links, Passwort vergessen) */
  accent: string;
};

function hexToRgb(hex: string) {
  const h = hex.replace("#", "").trim();
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function rgbToHex(r: number, g: number, b: number) {
  const to = (v: number) => v.toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase();
}
function channelLuma(c: number) {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}
function relativeLuminance(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  return 0.2126 * channelLuma(r) + 0.7152 * channelLuma(g) + 0.0722 * channelLuma(b);
}
function contrastHex(a: string, b: string) {
  const hi = Math.max(relativeLuminance(a), relativeLuminance(b));
  const lo = Math.min(relativeLuminance(a), relativeLuminance(b));
  return (hi + 0.05) / (lo + 0.05);
}
/** Mischt `background` in Richtung `toward`, bis der Kontrast `target` erreicht. */
function shadeForContrast(background: string, toward: string, target: number) {
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (contrastHex(mix(background, toward, mid), background) < target) lo = mid;
    else hi = mid;
  }
  return mix(background, toward, hi);
}
export function mix(a: string, b: string, t: number) {
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  const k = Math.max(0, Math.min(1, t));
  return rgbToHex(
    Math.round(A.r + (B.r - A.r) * k),
    Math.round(A.g + (B.g - A.g) * k),
    Math.round(A.b + (B.b - A.b) * k),
  );
}

/** Erzeugt aus 4 Palettenfarben die volle Farbmenge für ein Theme (Einsatzgebiete wie oben). */
function buildColorsFromPalette(
  palette: Palette,
  mode: "light" | "dark",
): Theme["color"] {
  const text = mode === "dark" ? "#F4F6F9" : "#0F1722";
  const textSec =
    mode === "dark"
      ? "rgba(244,246,249,.78)"
      : mix(palette.surface, text, 0.78);
  const muted =
    mode === "dark"
      ? "rgba(244,246,249,.58)"
      : mix(palette.surface, text, 0.58);
  const placeholder =
    mode === "dark"
      ? "rgba(244,246,249,.42)"
      : mix(palette.surface, text, 0.42);
  const border =
    mode === "dark" ? "rgba(255,255,255,.12)" : mix(palette.surface, text, 0.1);
  const borderStrong =
    mode === "dark"
      ? "rgba(255,255,255,.22)"
      : mix(palette.surface, text, 0.16);
  const surfaceHover =
    mode === "dark"
      ? "rgba(255,255,255,.08)"
      : mix(palette.surface, text, 0.06);
  /** Auf Akzent-Buttons: immer Weiß (Akzente sind gesättigt). */
  const buttonText = "#FFFFFF";
  const linkHover = mix(palette.accent, buttonText, 0.22);
  /** Gefüllte Buttons/Badges: im Dark Mode den Akzent vertiefen, damit weißer Text lesbar bleibt. */
  const filledAccent =
    mode === "dark" ? mix(palette.accent, "#000000", 0.2) : palette.accent;
  const buttonDisabledBg =
    mode === "dark"
      ? mix(palette.surface, "#000000", 0.35)
      : mix("#FFFFFF", palette.bg, 0.35);
  const subtleOn =
    mode === "dark"
      ? relativeLuminance(palette.surface) > relativeLuminance(palette.bg)
        ? palette.surface
        : palette.bg
      : relativeLuminance(palette.bg) < relativeLuminance(palette.surface)
        ? palette.bg
        : palette.surface;

  // Im Dark Mode: Input-Platzhalter/Label/Icon brauchen guten Kontrast (Hellgrau auf dunklem BG).
  const inputPlaceholderFg =
    mode === "dark" ? INPUT_ON_DARK_FG : mix(palette.surface, text, 0.42);
  const inputIconFg = mode === "dark" ? INPUT_ON_DARK_FG : muted;

  return {
    primary: palette.primary,
    primaryMuted: mix(palette.primary, palette.surface, 0.35),
    secondary: mix(palette.primary, text, 0.06),

    text,
    textSecondary: textSec,
    muted,
    placeholder,
    fine: muted,
    footer: muted,

    inputBg: palette.surface,
    inputBorder: border,
    inputFocusBorder: palette.accent,
    inputPlaceholder: inputPlaceholderFg,
    inputLabelBg: palette.surface,
    inputIcon: inputIconFg,
    inputLabelFloatingColor: palette.accent,

    bg: palette.bg,
    bgStrong: palette.primary,
    surface: palette.surface,
    surfaceStrong: palette.surface,
    surfaceHover,
    panelContentBg: mode === "light" ? "#FFFFFF" : palette.surface,
    sectionCardBodyBg: mode === "light" ? "#FFFFFF" : palette.surface,
    heroPanelBg: mode === "dark" ? "rgba(0,0,0,.4)" : "rgba(255,255,255,.56)",
    heroPaginationBg: "rgba(255,255,255,.45)",

    border,
    borderStrong,
    overlay: mode === "dark" ? "rgba(8,12,16,0.72)" : "rgba(15,27,34,0.55)",

    accent: palette.accent,
    accentMuted: mix(palette.accent, buttonText, 0.18),
    accentBg: palette.bg,

    success: mode === "dark" ? "#3FB98A" : "#8ae4c0",
    successBg:
      mode === "dark" ? "rgba(63,185,138,.18)" : "rgba(63,185,138,.12)",
    info: mode === "dark" ? "rgba(144,178,200,.7)" : "#4A7896",
    infoBg: mode === "dark" ? "rgba(144,178,200,.18)" : "rgba(74,120,150,.12)",
    warning: mode === "dark" ? "#E0A33A" : "#006be6",
    warningBg: mode === "dark" ? "rgba(224,163,58,.18)" : "rgba(0,107,230,.14)",
    warningText: mode === "dark" ? "#E0A33A" : "#006be6",
    danger: mode === "dark" ? "#E17672" : "#cc1111",
    dangerBg: mode === "dark" ? "rgba(225,118,114,.18)" : "rgba(204,17,17,.1)",
    dangerText: mode === "dark" ? "#E17672" : "#cc1111",
    dangerButtonBg: mode === "dark" ? "#B45452" : "#A4494A",
    successText: mode === "dark" ? "#3FB98A" : "#8ae4c0",
    infoText: mode === "dark" ? "rgba(144,178,200,.7)" : "#4A7896",

    buttonBg: filledAccent,
    buttonText,
    buttonDisabledBg,
    buttonDisabledText: shadeForContrast(buttonDisabledBg, text, 5),
    buttonDisabledSubtle: shadeForContrast(subtleOn, text, 4.5),
    buttonDisabledSelectedBorder: mode === "dark" ? "#F4F6F9" : text,
    buttonBgSecondary: mix(
      palette.primary,
      palette.accent,
      mode === "dark" ? 0.22 : 0.12,
    ),
    buttonTextSecondary: text,
    outlineButtonBg: "transparent",
    outlineButtonBorder: palette.accent,

    link: palette.accent,
    linkHover,

    white: "#FFFFFF",
    black: "#0B1116",

    badgeBg: filledAccent,
    badgeText: buttonText,
    badgeMutedBg: mix(palette.primary, palette.accent, 0.12),
    badgeMutedText: muted,
  };
}

const WALD_LIGHT: Omit<Theme, "mode"> = {
  ...themeBase,
  color: buildColorsFromPalette(
    {
      bg: "#C8D9CC",
      surface: "#F2F6F3",
      primary: "#7DA889",
      accent: "#2D7A4F",
    },
    "light",
  ),
  shadow: { color: "#0F1722", opacity: 0.07, radius: 12, offset: { x: 0, y: 2 } },
  panelShadow: NO_PANEL_SHADOW,
  cardShadow: {},
  primaryButtonShadow: {},
  outlineButtonShadow: {},
  inputFocusShadow: {},
  pageBgWebGradient: undefined,
};

const WALD_DARK: Omit<Theme, "mode"> = {
  ...themeBase,
  color: buildColorsFromPalette(
    {
      bg: "#1E2F24",
      surface: "#283D2F",
      primary: "#33503C",
      accent: "#4CAF6A",
    },
    "dark",
  ),
  shadow: { color: "#000000", opacity: 0.32, radius: 16, offset: { x: 0, y: 4 } },
  panelShadow: NO_PANEL_SHADOW,
  cardShadow: {},
  primaryButtonShadow: {},
  outlineButtonShadow: {},
  inputFocusShadow: {},
  pageBgWebGradient: undefined,
};

const OZEAN_LIGHT: Omit<Theme, "mode"> = {
  ...themeBase,
  color: buildColorsFromPalette(
    {
      bg: "#C5D5DD",
      surface: "#F0F5F8",
      primary: "#7EA8BD",
      accent: "#1A8A8A",
    },
    "light",
  ),
  shadow: { color: "#0F1722", opacity: 0.07, radius: 12, offset: { x: 0, y: 2 } },
  panelShadow: NO_PANEL_SHADOW,
  cardShadow: {},
  primaryButtonShadow: {},
  outlineButtonShadow: {},
  inputFocusShadow: {},
  pageBgWebGradient: undefined,
};

const OZEAN_DARK: Omit<Theme, "mode"> = {
  ...themeBase,
  color: buildColorsFromPalette(
    {
      bg: "#1A2A33",
      surface: "#233845",
      primary: "#2D4A5A",
      accent: "#2AADAD",
    },
    "dark",
  ),
  shadow: { color: "#000000", opacity: 0.32, radius: 16, offset: { x: 0, y: 4 } },
  panelShadow: NO_PANEL_SHADOW,
  cardShadow: {},
  primaryButtonShadow: {},
  outlineButtonShadow: {},
  inputFocusShadow: {},
  pageBgWebGradient: undefined,
};

const LAVENDEL_LIGHT: Omit<Theme, "mode"> = {
  ...themeBase,
  color: buildColorsFromPalette(
    {
      bg: "#D4D0E4",
      surface: "#F5F3FA",
      primary: "#A69DC2",
      accent: "#7B5EA7",
    },
    "light",
  ),
  shadow: { color: "#0F1722", opacity: 0.07, radius: 12, offset: { x: 0, y: 2 } },
  panelShadow: NO_PANEL_SHADOW,
  cardShadow: {},
  primaryButtonShadow: {},
  outlineButtonShadow: {},
  inputFocusShadow: {},
  pageBgWebGradient: undefined,
};

const LAVENDEL_DARK: Omit<Theme, "mode"> = {
  ...themeBase,
  color: buildColorsFromPalette(
    {
      bg: "#262038",
      surface: "#322A48",
      primary: "#3E355B",
      accent: "#9B7ED8",
    },
    "dark",
  ),
  shadow: { color: "#000000", opacity: 0.32, radius: 16, offset: { x: 0, y: 4 } },
  panelShadow: NO_PANEL_SHADOW,
  cardShadow: {},
  primaryButtonShadow: {},
  outlineButtonShadow: {},
  inputFocusShadow: {},
  pageBgWebGradient: undefined,
};

const ERDE_LIGHT: Omit<Theme, "mode"> = {
  ...themeBase,
  color: buildColorsFromPalette(
    {
      bg: "#DDD5CA",
      surface: "#F8F5F0",
      primary: "#B8A48E",
      accent: "#A16B2A",
    },
    "light",
  ),
  shadow: { color: "#0F1722", opacity: 0.07, radius: 12, offset: { x: 0, y: 2 } },
  panelShadow: NO_PANEL_SHADOW,
  cardShadow: {},
  primaryButtonShadow: {},
  outlineButtonShadow: {},
  inputFocusShadow: {},
  pageBgWebGradient: undefined,
};

const ERDE_DARK: Omit<Theme, "mode"> = {
  ...themeBase,
  color: buildColorsFromPalette(
    {
      bg: "#2A2218",
      surface: "#3A2F22",
      primary: "#4D3F2E",
      accent: "#C98A3D",
    },
    "dark",
  ),
  shadow: { color: "#000000", opacity: 0.32, radius: 16, offset: { x: 0, y: 4 } },
  panelShadow: NO_PANEL_SHADOW,
  cardShadow: {},
  primaryButtonShadow: {},
  outlineButtonShadow: {},
  inputFocusShadow: {},
  pageBgWebGradient: undefined,
};

const ROSE_LIGHT: Omit<Theme, "mode"> = {
  ...themeBase,
  color: buildColorsFromPalette(
    {
      bg: "#E0D0D5",
      surface: "#FAF3F5",
      primary: "#C09AA8",
      accent: "#C04670",
    },
    "light",
  ),
  shadow: { color: "#0F1722", opacity: 0.07, radius: 12, offset: { x: 0, y: 2 } },
  panelShadow: NO_PANEL_SHADOW,
  cardShadow: {},
  primaryButtonShadow: {},
  outlineButtonShadow: {},
  inputFocusShadow: {},
  pageBgWebGradient: undefined,
};

const ROSE_DARK: Omit<Theme, "mode"> = {
  ...themeBase,
  color: buildColorsFromPalette(
    {
      bg: "#2D1F25",
      surface: "#3D2B33",
      primary: "#503A44",
      accent: "#E05A88",
    },
    "dark",
  ),
  shadow: { color: "#000000", opacity: 0.32, radius: 16, offset: { x: 0, y: 4 } },
  panelShadow: NO_PANEL_SHADOW,
  cardShadow: {},
  primaryButtonShadow: {},
  outlineButtonShadow: {},
  inputFocusShadow: {},
  pageBgWebGradient: undefined,
};

// =============================================================================
// Preset-Liste und Auflösung
// =============================================================================

export type ThemePreset = {
  id: string;
  label: string;
  light: Omit<Theme, "mode">;
  dark: Omit<Theme, "mode">;
};

export const THEME_PRESETS: ThemePreset[] = [
  /** P1 Arbeits-Theme: medizinisches Blaugrün, flach, dicht (aus Klar abgeleitet). */
  { id: "care", label: "Care", light: KLAR_LIGHT, dark: KLAR_DARK },
  {
    id: "standard",
    label: "Standard",
    light: STANDARD_LIGHT,
    dark: STANDARD_DARK,
  },
  { id: "klar", label: "Klar", light: KLAR_LIGHT, dark: KLAR_DARK },
];

const DEFAULT_PRESET = THEME_PRESETS[0];

export function resolveTheme(presetId: string, mode: "light" | "dark"): Theme {
  const preset = THEME_PRESETS.find((p) => p.id === presetId) ?? DEFAULT_PRESET;
  const base = mode === "light" ? preset.light : preset.dark;
  return { ...base, mode };
}

export const lightTheme = resolveTheme("care", "light");
export const darkTheme = resolveTheme("care", "dark");

/**
 * Theme-aware Seiten-Hintergrund-Gradient (oben-links heller → unten-rechts bg).
 * Light: deutlich heller oben für sanften Verlauf; Dark/Presets: dezenter Lift.
 * Funktioniert für alle Presets, da aus theme.color.bg abgeleitet.
 */
export function getScreenGradientColors(theme: Theme): [string, string] {
  // Flacher Look: kein sichtbarer Verlauf – Inhalt steht ruhig, kein Ablenken.
  if (theme.flatBackground) return [theme.color.bg, theme.color.bg];
  const top = mix(theme.color.bg, "#FFFFFF", theme.mode === "light" ? 0.55 : 0.06);
  return [top, theme.color.bg];
}
